import { Typography } from '@mui/material';
import Grid from 'components/core/Grid';

import { TwilioVerificationChannelEnum, UserRoleIDEnum, useTwilioSendCodeMutation } from '@alluradirect/react';

import { Form, Formik } from 'formik';
import { useListModalContext } from '../listModalContext';
import SelectSetupType from './SelectSetupType';
import { useListPageModalNavigation } from '../hooks/useListPageModalNavigation';
import { ListPageUserFormInputs } from './ListPageUserFormInputs';
import { FormikAppTextField } from 'components/formik/core/FormikAppTextField/FormikAppTextField';
import * as yup from 'yup';
import { UserSignUpTermsOfService } from 'components/views/pages/signup/components/UserSignUpTermsOfService';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import { SetupItem } from 'components/views/forms/styles';
import { useState } from 'react';
import { toast } from 'react-toastify';
import AlluraTurnstile from 'components/layout/Page/components/AlluraTurnstile';
import { verifyTurnstileToken } from 'components/views/pages/_general/helpers';

const validationSchema = yup.object().shape({
  email: yup.string().email().min(3).max(100).required().label('Email'),
  firstName: yup.string().max(100).required().label('First Name'),
  lastName: yup.string().max(100).required().label('Last Name'),
  listingUrl: yup.string().max(100).required().label('Listing URL'),
});

export const ListUserCreateFormAssisted = () => {
  const sendTwilioVerification = useTwilioSendCodeMutation();

  const { pushNextStep } = useListPageModalNavigation();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const {
    listFormValues: { email, firstName, lastName },
    setListFormValues,
  } = useListModalContext();

  const onSubmit = async (value: any) => {
    if (!captchaToken) {
      toast.error("Please complete the safety check.");
      return;
    }

    try {
      const turnstileData = await verifyTurnstileToken(captchaToken, 'list');
      if (!turnstileData?.SUCCESS || !turnstileData?.DATA?.SUCCESS) {
        toast.error("Server rejected the request or captcha failed.");
        return;
      }
    } catch (e) {
      toast.error("Error connecting to the backend server.");
      return;
    }

    setListFormValues(prev => ({
      ...prev,
      email: value.email,
      firstName: value.firstName,
      lastName: value.lastName,
      listingUrl: value.listingUrl,
    }));

    sendTwilioVerification.mutate(
      { value: value.email, channel: TwilioVerificationChannelEnum.email },
      { onSuccess: pushNextStep },
    );
  };

  const initialValues = {
    email,
    firstName,
    lastName,
    listingUrl: '',
    is_email_taken: false,
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <SelectSetupType />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" mb={1}>
          Our Team is Standing By
        </Typography>
        <Typography variant="body2">
          If you&apos;re already a host on another website, complete the information below and one of our team members
          will begin the setup process. You will receive an email update within 1-2 business days with the final
          activation steps.
        </Typography>
      </Grid>
      <SetupItem item xs={12}>
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({ values }) => (
            <Form>
              <Grid item xs={12}>
                <br />
              </Grid>
              <Grid container spacing={2}>
                <ListPageUserFormInputs />
                <Grid item xs={12}>
                  <FormikAppTextField name="listingUrl" label="Property Listing URL" />
                </Grid>
                <Grid item xs={12}>
                  <AlluraTurnstile onVerify={(token) => setCaptchaToken(token)} siteKey={process.env.NEXT_PUBLIC_TURNSTILE_LIST_KEY ?? ''} />
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    isFormikButton
                    isFullWidth
                    buttonColor="secondary"
                    variant="contained"
                    size="large"
                    isLoading={sendTwilioVerification.isLoading}
                    isDisabled={values.is_email_taken || !captchaToken}
                  >
                    Continue
                  </LoadingButton>
                </Grid>
                <Grid item xs={12}>
                  <UserSignUpTermsOfService userTypeId={UserRoleIDEnum.owner} />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </SetupItem>
    </Grid>
  );
};
